import React, { useEffect, useState, useCallback } from "react";
import { Card, Row, Col, Form, Button, Spinner } from "react-bootstrap";
import Header from "../layouts/Header";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";

const API    = process.env.REACT_APP_API;
const SOCKET = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3002';

const GROUP_LABELS = {
  school:    'ข้อมูลโรงเรียน',
  principal: 'ผู้อำนวยการ',
  contact:   'ข้อมูลติดต่อ',
};

export default function AdminSettings() {
  const { setLoading } = useLoading();
  const [settings, setSettings]   = useState({});   // { key: value }
  const [labels, setLabels]       = useState({});   // { key: label }
  const [groups, setGroups]       = useState({});   // { group: [key, ...] }
  const [saved, setSaved]         = useState({});   // { key: bool }
  const [savingGroup, setSavingGroup] = useState(null);

  // Logo
  const [logoFile, setLogoFile]   = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoMsg, setLogoMsg]     = useState('');

  const load = useCallback(() => {
    setLoading(true);
    axiosInstance.get(`${API}/admin/getSettings`)
      .then(res => {
        const sv = {}, sl = {}, sg = {};
        res.data.forEach(row => {
          sv[row.setting_key]   = row.setting_value ?? '';
          sl[row.setting_key]   = row.setting_label;
          if (!sg[row.setting_group]) sg[row.setting_group] = [];
          sg[row.setting_group].push(row.setting_key);
        });
        setSettings(sv);
        setLabels(sl);
        setGroups(sg);
        if (sv.school_logo) {
          setLogoPreview(`${SOCKET}/uploads/${sv.school_logo}`);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [setLoading]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (key, value) => {
    setSettings(s => ({ ...s, [key]: value }));
  };

  const saveGroup = async (group) => {
    setSavingGroup(group);
    const keys = (groups[group] || []).filter(k => k !== 'school_logo');
    try {
      await Promise.all(keys.map(key =>
        axiosInstance.post(`${API}/admin/updateSetting`, { setting_key: key, setting_value: settings[key] })
      ));
      setSaved(s => ({ ...s, [group]: true }));
      setTimeout(() => setSaved(s => ({ ...s, [group]: false })), 2500);
    } catch {}
    setSavingGroup(null);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setLogoMsg('');
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    setUploadingLogo(true);
    setLogoMsg('');
    const fd = new FormData();
    fd.append('logo', logoFile);
    try {
      const res = await axiosInstance.post(`${API}/admin/uploadLogo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoMsg(res.data.message || 'อัปโหลดสำเร็จ');
      setLogoFile(null);
      setSettings(s => ({ ...s, school_logo: res.data.filename }));
    } catch (err) {
      setLogoMsg(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    }
    setUploadingLogo(false);
  };

  const renderGroup = (group) => {
    const keys = (groups[group] || []).filter(k => k !== 'school_logo');
    if (keys.length === 0) return null;
    const isRtlGroup = (key) => key.endsWith('_ar');
    return (
      <Card className="card-one mb-3" key={group}>
        <Card.Header>
          <h6 className="mb-0">{GROUP_LABELS[group] || group}</h6>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            {keys.map(key => (
              <Col md={6} key={key}>
                <Form.Group>
                  <Form.Label className="text-secondary small mb-1">{labels[key]}</Form.Label>
                  <Form.Control
                    value={settings[key] ?? ''}
                    onChange={e => handleChange(key, e.target.value)}
                    dir={isRtlGroup(key) ? 'rtl' : undefined}
                    className={isRtlGroup(key) ? 'arabic-text' : ''}
                  />
                </Form.Group>
              </Col>
            ))}
          </Row>
        </Card.Body>
        <Card.Footer className="d-flex align-items-center justify-content-end gap-2">
          {saved[group] && <span className="text-success small"><i className="ri-check-line me-1"></i>บันทึกแล้ว</span>}
          <Button size="sm" variant="primary" onClick={() => saveGroup(group)} disabled={savingGroup === group}>
            {savingGroup === group ? <Spinner animation="border" size="sm" /> : 'บันทึก'}
          </Button>
        </Card.Footer>
      </Card>
    );
  };

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
      <h4 className="main-title mb-4">ตั้งค่าระบบ</h4>

      {Object.keys(groups).filter(g => g !== 'school').map(g => renderGroup(g))}
      {groups.school && renderGroup('school')}

      {/* Logo upload */}
      <Card className="card-one">
        <Card.Header>
          <h6 className="mb-0">โลโก้โรงเรียน</h6>
        </Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-center">
            <Col xs="auto">
              {logoPreview ? (
                <img src={logoPreview} alt="logo" style={{ height: 80, width: 80, objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: 8, background: '#f8f9fa' }} />
              ) : (
                <div style={{ height: 80, width: 80, border: '1px dashed #adb5bd', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="ri-image-line text-muted fs-4"></i>
                </div>
              )}
            </Col>
            <Col>
              <Form.Control type="file" accept="image/*" onChange={handleLogoChange} className="mb-2" />
              <small className="text-muted">PNG, JPG, SVG — ไม่เกิน 2 MB</small>
              {logoMsg && <div className="mt-1 small text-success">{logoMsg}</div>}
            </Col>
            <Col xs="auto">
              <Button size="sm" variant="primary" onClick={uploadLogo} disabled={!logoFile || uploadingLogo}>
                {uploadingLogo ? <Spinner animation="border" size="sm" /> : 'อัปโหลด'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      </div>
    </React.Fragment>
  );
}
